import { DOCUMENT, Injectable, inject } from '@angular/core';

import { MatchingSession } from '@data/session/session.model';
import { SessionService } from '@data/session/session.service';
import { contrastInk, normalizeHexColor } from '@shared/util/color.util';

/** The custom properties every fair-coloured rule in `styles.scss` reads. */
const FAIR_PROPERTIES = [
  '--fair-primary',
  '--fair-primary-ink',
  '--fair-accent',
  '--fair-accent-ink',
] as const;

/**
 * Paints the onboarding pages in the fair's own colours.
 *
 * The colours live on the session's document-generator config. They are written onto `<body>`
 * as CSS custom properties (not into a component's styles) because the surrounding layout,
 * Material's own component tokens and the dialogs opened from these pages all sit outside any
 * single component's style scope — and are removed again when the onboarding area is left, so
 * the operator UI keeps the brand palette.
 */
@Injectable({ providedIn: 'root' })
export class FairColorsService {
  private document = inject(DOCUMENT);
  private sessionRepo = inject(SessionService);

  /** Session whose colours are currently on `<body>` — keeps navigation from re-fetching. */
  private appliedFor: string | null = null;

  /** Look the session up on the public route and paint its colours. */
  applyForSession(sessionId: string | null): void {
    if (!sessionId) {
      this.clear();
      return;
    }
    if (sessionId === this.appliedFor) return;
    this.appliedFor = sessionId;
    this.sessionRepo.getById(sessionId, undefined, true).subscribe({
      // A fair without colours (or an unreachable session) simply keeps the brand palette.
      next: (session) => this.apply(session),
      error: () => undefined,
    });
  }

  apply(session?: MatchingSession): void {
    const primary = normalizeHexColor(session?.generatorConfig?.primary);
    const accent = normalizeHexColor(session?.generatorConfig?.secondary);
    const style = this.document.body.style;
    if (primary) {
      style.setProperty('--fair-primary', primary);
      style.setProperty('--fair-primary-ink', contrastInk(primary));
    }
    if (accent) {
      style.setProperty('--fair-accent', accent);
      style.setProperty('--fair-accent-ink', contrastInk(accent));
    }
  }

  clear(): void {
    this.appliedFor = null;
    for (const property of FAIR_PROPERTIES) {
      this.document.body.style.removeProperty(property);
    }
  }
}
